import ImageBrokenIcon from "@redotech/redo-icons/alert-triangle.svg";
import FileIcon from "@redotech/redo-icons/file-02.svg";
import { isSafeHttpUrl } from "@redotech/util/link";
import type { FileUIPart } from "ai";
import classNames from "classnames";
import { memo, useState } from "react";
import * as styles from "./ask-redo-file-part.module.css";

/**
 * Renders a single file/image attachment part of an assistant message.
 *
 * - Image attachments open the original in a new tab on click (zoom).
 * - Failed image loads collapse to a small "couldn't load" placeholder
 *   instead of leaving a bare alt text or broken-image glyph.
 * - Non-image attachments render as a labeled link with a derived
 *   filename + extension chip when the part has no `filename`.
 */
export const AskRedoFilePart = memo(function AskRedoFilePart({
  part,
}: {
  part: FileUIPart;
}) {
  const [imageState, setImageState] = useState<"loading" | "loaded" | "error">(
    "loading",
  );

  // Drop any unsafe scheme (javascript:, data:, etc.) before
  // handing the URL to the DOM — see util/safe-url.
  if (!isSafeHttpUrl(part.url)) {
    return null;
  }

  const isImage = part.mediaType?.startsWith("image/");
  const filename = part.filename ?? deriveFilenameFromUrl(part.url);

  if (isImage) {
    if (imageState === "error") {
      return (
        <div
          aria-label="Image failed to load"
          className={styles.imageError}
          role="img"
        >
          <ImageBrokenIcon
            aria-hidden="true"
            className={styles.imageErrorIcon}
          />
          <span>Couldn't load image</span>
        </div>
      );
    }
    return (
      <a
        aria-label={filename ? `Open ${filename}` : "Open image"}
        className={styles.imageLink}
        href={part.url}
        rel="noopener noreferrer"
        target="_blank"
      >
        <img
          alt={filename ?? ""}
          className={classNames(
            styles.image,
            imageState === "loaded" && styles.imageLoaded,
          )}
          decoding="async"
          loading="lazy"
          onError={() => setImageState("error")}
          onLoad={() => setImageState("loaded")}
          referrerPolicy="no-referrer"
          src={part.url}
        />
      </a>
    );
  }

  const ext = extractExtension(filename);
  return (
    <a
      className={styles.fileLink}
      href={part.url}
      rel="noopener noreferrer"
      target="_blank"
    >
      <FileIcon aria-hidden="true" className={styles.fileIcon} />
      <span className={styles.fileName}>{filename ?? "Attachment"}</span>
      {ext && <span className={styles.fileExt}>{ext}</span>}
    </a>
  );
});

function deriveFilenameFromUrl(url: string): string | undefined {
  try {
    const pathname = new URL(url).pathname;
    const last = pathname.split("/").findLast(Boolean);
    if (!last) {
      return undefined;
    }
    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  } catch {
    return undefined;
  }
}

function extractExtension(filename: string | undefined): string | null {
  if (!filename) {
    return null;
  }
  const dot = filename.lastIndexOf(".");
  if (dot < 0 || dot === filename.length - 1) {
    return null;
  }
  const ext = filename.slice(dot + 1);
  // Skip absurdly long "extensions" (likely not real)
  if (ext.length > 5) {
    return null;
  }
  return ext.toLowerCase();
}
